import { Box, Flex, Grid, LinkBox, LinkOverlay, SimpleGrid } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import { brandConfig } from "../../brandConfig";
import { useAuth } from "../../contexts/AuthContext";
import { EVENTS, HomeEvent, SOP_PILLS } from "./homeContent";
import { HOME_PUBLIC_SOP_EPISODES, HomePublicSopEpisode, HomePublicSopEpisodesData, HomePublicSopEpisodesVars } from "./queries";
import { Body, Cta, H2, H3, Inner, Kicker, Lead, Mono, Section, alpha, useHomeTokens } from "./primitives";

/** Brisbane-anchored day stamp for an episode's publish time ("27 AUG 2026"). Display only — storage is UTC. */
const brisbaneDay = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Australia/Brisbane",
});

const formatPublished = (iso?: string | null): string => {
  if (!iso) return "PUBLISHED";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "PUBLISHED";
  return brisbaneDay.format(d).toUpperCase();
};

/** Shape a live episode into the same card the static design uses. */
const toEvent = (ep: HomePublicSopEpisode): HomeEvent & { slug: string } => ({
  slug: ep.slug,
  time: formatPublished(ep.publishedAt),
  place: ep.episodeNumber ? `EP ${ep.episodeNumber}` : "EPISODE",
  title: ep.headline || ep.title,
  body: ep.subtitle || "",
});

interface EventCardProps {
  event: HomeEvent & { slug?: string };
}

const EventCard = ({ event }: EventCardProps) => {
  const t = useHomeTokens();
  const inner = (
    <Grid
      templateColumns="auto 1fr"
      gap="20px"
      alignItems="start"
      p="22px 24px"
      borderRadius="16px"
      borderWidth="1px"
      borderColor={t.cardBorder}
      bg={t.cardGradient}
      transition="border-color .2s ease"
      _hover={event.slug ? { borderColor: t.primaryBorder } : undefined}
    >
      <Flex direction="column" gap="4px" pr="20px" borderRight="1px solid" borderColor={t.hairline} minW="82px">
        <Mono color={t.primary}>{event.time}</Mono>
        <Mono fontSize="10.5px" letterSpacing=".08em" color={t.textFaint}>
          {event.place}
        </Mono>
      </Flex>
      <Box minW={0}>
        <H3 fontSize="1rem" mb="6px">
          {event.slug ? (
            <LinkOverlay
              as={RouterLink}
              to={`/play/${event.slug}`}
              title="Opens this State of Play episode's public replay page. Reading only."
            >
              {event.title}
            </LinkOverlay>
          ) : (
            event.title
          )}
        </H3>
        <Body fontSize=".88rem" lineHeight="1.55" color={t.textMuted}>
          {event.body}
        </Body>
      </Box>
    </Grid>
  );
  return event.slug ? <LinkBox>{inner}</LinkBox> : inner;
};

/**
 * #stateofplay — "A different category of record."
 *
 * Cards are LIVE when the public catalogue has published episodes
 * (`publicSopEpisodes`, no login needed); the design's static events are the
 * loading / error / empty fallback so the section never renders bare.
 */
export const StateOfPlay = () => {
  const t = useHomeTokens();
  const { isAuthenticated } = useAuth();
  const { data } = useQuery<HomePublicSopEpisodesData, HomePublicSopEpisodesVars>(HOME_PUBLIC_SOP_EPISODES, {
    variables: { tenantId: brandConfig.tenantId, limit: 4 },
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  const live = (data?.publicSopEpisodes ?? []).filter((ep) => ep && ep.slug);
  // The static fallback's "BB distribution round" card is member-only investment language (homeContent.ts).
  const fallback = EVENTS.filter((ev) => isAuthenticated || !ev.memberOnly);
  const events: Array<HomeEvent & { slug?: string }> = live.length > 0 ? live.map(toEvent) : fallback;

  return (
    <Section id="stateofplay">
      <Box
        position="absolute"
        left="-10%"
        right="-10%"
        bottom="-40%"
        h="600px"
        pointerEvents="none"
        bg={`radial-gradient(50% 50% at 50% 100%, ${alpha(t.primary, 0.14)} 0%, transparent 70%)`}
      />
      <Inner>
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: "40px", lg: "56px" }} alignItems="center">
          <Flex direction="column" gap="20px">
            <Kicker>New information medium</Kicker>
            <H2>State of Play</H2>
            <Lead>
              A different category of record. Where the news cycle gives you opinion, State of Play events give you the
              position: what changed, who it affects, and what it means for where you are right now.
            </Lead>
            <Lead>
              Because it does matter where you are. Every event is placed, timed and signed, so the account of what
              happened holds up long after the noise moves on.
            </Lead>
            <Flex wrap="wrap" gap="10px" mt="6px">
              {SOP_PILLS.map((pill) => (
                <Box
                  key={pill}
                  as="span"
                  px="14px"
                  py="8px"
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor={alpha(t.text, 0.12)}
                  fontSize=".85rem"
                  color={t.textSoft}
                >
                  {pill}
                </Box>
              ))}
            </Flex>
            <Box mt="14px">
              <Cta
                to="/play"
                variant="ghost"
                arrow
                title="Opens the public State of Play catalogue — every published episode with its replay. Reading only."
              >
                Follow State of Play
              </Cta>
            </Box>
          </Flex>

          <Box display="grid" gap="12px">
            {events.map((e) => (
              <EventCard key={e.slug ?? e.title} event={e} />
            ))}
          </Box>
        </SimpleGrid>
      </Inner>
    </Section>
  );
};
